// Copyright ©️ 2026 eVolve MEP, LLC
import type { StrongOmit } from '@evolvemep/api-ts';
import { Button } from '@mantine/core';

import type { ShopNavigationRoute } from 'app/ModuleRoutes';
import { MobileLinkButton, type MobileLinkButtonProps } from 'components/Mantine/MobileLinkButton';

import type { PageTitle } from './Navigation/types';
import { useEvolveNavigate } from './Navigation/useEvolveNavigate';

type Props<T> = {
  /** The title of the page the user is navigating from. Used to populate breadcrumbs. */
  from: string | PageTitle;
  /** See the clearHistory param in {@link useEvolveNavigate} for details on this prop */
  clearHistory?: Parameters<typeof useEvolveNavigate>[1];
  linkButtons: (StrongOmit<MobileLinkButtonProps<T>, 'onClick'> & {
    /** The route the user is navigating to. Must be unique since this is used as the key in the list */
    to: ShopNavigationRoute;
  })[];
};

/** A group of {@link MobileLinkButton} components, displayed vertically */
export const MobileLinkButtonGroup = <T = undefined,>({ from, linkButtons, clearHistory }: Props<T>) => {
  const navigate = useEvolveNavigate(from, clearHistory);
  return (
    <Button.Group orientation="vertical">
      {linkButtons.map(({ to, ...rest }) => (
        <MobileLinkButton key={to} onClick={() => navigate(to)} {...rest} />
      ))}
    </Button.Group>
  );
};
